import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Link, Paper, TextField, Typography } from '@mui/material';
import { authApi, RESET_EMAIL_KEY } from '../../api/auth';
import { APP_NAME } from '../../constants';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Registered email address is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await authApi.forgotPassword(normalizedEmail);
      sessionStorage.setItem(RESET_EMAIL_KEY, normalizedEmail);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        backgroundColor: 'background.default',
      }}
    >
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: 4, borderRadius: 3 }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Forgot Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your registered email and we will send a one-time password (OTP).
        </Typography>

        {sent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            OTP has been sent to {email}. Continue to verify your OTP.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Registered Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(error)}
              helperText={error}
              required
              margin="normal"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="secondary"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? 'Sending…' : 'Send OTP'}
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to Login
          </Link>
          {sent && (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                navigate('/verify-otp', { state: { email: email.trim().toLowerCase() } })
              }
            >
              Verify OTP
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          {APP_NAME} Admin
        </Typography>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
